import type { KeyboardEvent, SyntheticEvent } from 'react';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import type { Question } from '@omq/types';
import {
  isFocusVisible,
  useBackendContext,
  useComponentMounted,
  useConfigContext,
  useEventContext,
} from '@omq/shared';

import { ContactAPI } from '../../api/contact';
import { AnswerList } from '../answer-list/answer-list';
import { ContactEvents } from '../../events/contact-events';
import { ConfigAnswerDisplayTypes } from '@omq/types';
import { usePrintQuestion } from '@omq/shared/src/hooks/print-question';

// type for selection handler
type QuestionSelectionHandler = (id: Question | null) => void;

/**
 * Type for component properties.
 */
type QuestionListItemProps = {
  question: Question & { isLoading?: boolean };
  isActive: boolean;
  onSelect: QuestionSelectionHandler;
};
/**
 * Renders content of a single question.
 *
 * @param {QuestionListItemProps} props - Component properties
 *
 * @author Florian Walch
 * @since 9.3
 *
 * @returns {JSX.Element}
 */
export function QuestionListItem({
  question,
  isActive,
  onSelect,
}: QuestionListItemProps): JSX.Element {
  const config = useConfigContext();
  const backend = useBackendContext();
  const eventDispatcher = useEventContext<ContactEvents>();
  const linkRef = useRef<HTMLAnchorElement>();

  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState<boolean>(false);

  // store focus state of the header link, it drives the focus style class.
  // Only set for keyboard focus (:focus-visible), not for mouse clicks.
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  // remember the previous active state to detect a collapse
  const wasActive = useRef<boolean>(isActive);

  // Restore keyboard focus to the question header link when the question
  // collapses while the focus was inside the removed answer content -
  // otherwise the focus falls back to document.body and keyboard users
  // lose their position. Layout effect: runs after the unmount but before
  // paint, so focus never visibly sits on <body>.
  useLayoutEffect(() => {
    if (wasActive.current && !isActive) {
      const activeElement = document.activeElement;

      if ((activeElement == null || activeElement === document.body) && linkRef.current != null) {
        linkRef.current.focus();
      }
    }

    wasActive.current = isActive;
  }, [isActive]);

  // Opens the browser's print dialog and applies print specific CSS
  // to be able to print the active item
  usePrintQuestion({
    isPrintEnabled: isPrintDialogOpen,
    setIsPrintEnabled: setIsPrintDialogOpen,
    filePath: '/contact/assets/print-style.css',
  });

  // scroll to activated item
  useComponentMounted(() => {
    const link = linkRef.current as HTMLElement;
    if (isActive && link != null && link.scrollIntoView != null) {
      link.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isActive]);

  // build class name
  let className = config.generateClassName('question-list-item');
  if (isActive) {
    className += ` ${config.generateClassName('question-list-item--is-active')}`;
  }
  if (hasFocus) {
    className += ` ${config.generateClassName('question-list-item--focus')}`;
  }

  const isInline = config.answerDisplayType === ConfigAnswerDisplayTypes.INLINE;

  // Rotate the toggle icon: For popup, it points to the right,
  // for inline, it points to the bottom when closed and to the top when opened
  let toggleIconClassName = config.generateClassName('question-list__toggle-icon');

  if (isInline) {
    toggleIconClassName += isActive
      ? ` ${config.generateClassName('question-list__toggle-icon--up')}`
      : ` ${config.generateClassName('question-list__toggle-icon--down')}`;
  }

  // create click handler
  const handleClick = (event: SyntheticEvent | KeyboardEvent) => {
    event.preventDefault();

    // Do not close the question if the print button has been clicked
    if (
      onSelect != null &&
      !(event.target as HTMLElement)?.classList.contains('omq-question-list__print-button') &&
      !(event.target as HTMLElement)?.classList.contains('omq-question-list__print-icon')
    ) {
      onSelect(isActive ? null : question);
    }

    if (!isActive) {
      eventDispatcher.dispatchQuestionClick(question);
      ContactAPI.trackView(backend, question.id);
    }
  };

  // Keyboard handler: role="button" elements must activate on Space as
  // well as Enter. preventDefault on Space stops page scroll (handleClick
  // calls preventDefault anyway, but keep activation and default handling
  // in one place).
  const handleKeyDown = (event: KeyboardEvent) => {
    // ignore keys bubbling up from descendants (e.g. the inline print
    // button) - preventDefault on those would cancel the descendant's
    // own native keyboard activation
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event);
    }
  };

  // render component
  return (
    <Fragment>
      <div className={config.generateClassName('question-list-item__wrapper')}>
        <a
          className={className}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-controls={`aria-answers-${question.id}`}
          id={`aria-question-${question.id}`}
          aria-expanded={isActive}
          title={question.body}
          ref={linkRef}
          onKeyDown={handleKeyDown}
          onFocus={(event) => setHasFocus(isFocusVisible(event.target as Element))}
          onBlur={() => setHasFocus(false)}>
          {question.isLoading && (
            <i className={config.generateClassName('question-list__loading-icon')} />
          )}
          <p className={config.generateClassName('question-list-item__body')}>{question.body}</p>
          {isActive && config.isPrintingActive && isInline && (
            <button
              onClick={() => setIsPrintDialogOpen(true)}
              title={config.loc('print')}
              className={config.generateClassName('question-list__print-button')}>
              <i className={config.generateClassName('question-list__print-icon')} />
            </button>
          )}
          <span className={toggleIconClassName} />
        </a>

        {isActive && (
          <AnswerList
            question={question}
            headline={question.body}
            onClose={() => {
              onSelect(null);
              if (linkRef.current != null) {
                linkRef.current.focus();
              }
            }}
          />
        )}
      </div>
    </Fragment>
  );
}
